import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { BOQProgress } from "@/pages/BOQManagement";
import { toast } from "sonner";
import {
  Package,
  TrendUp,
  TrendDown,
  Warning,
  MagnifyingGlass,
  Plus,
  CaretLeft,
  CaretRight,
  ClockCounterClockwise,
  Trash,
  PencilSimple,
  CurrencyInr,
  Calendar,
} from "@phosphor-icons/react";

const fmtQty = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n || 0);
const fmtINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const todayISO = () => new Date().toISOString().slice(0, 10);

const TYPE_META = {
  opening_stock: { label: "Opening Stock", color: "bg-[#E0E7FF] text-[#3730A3] border-[#A5B4FC]" },
  stock_in: { label: "Stock In", color: "bg-[#E6F4EA] text-[#137333] border-[#A4D0B3]" },
  stock_out: { label: "Stock Out", color: "bg-[#FEF7E0] text-[#B06000] border-[#FCE39E]" },
  damage: { label: "Damage", color: "bg-[#FCE8E6] text-[#C5221F] border-[#F3B2AC]" },
};

// Which adjustment types subtract from available stock (UI badge styling only)
const SUBTRACTIVE_TYPES = new Set(["stock_out", "damage"]);

export default function Stock() {
  const { user } = useAuth();
  const { activeProjectId, activeProject } = useProject();
  const isAdmin = user?.role === "admin";

  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [category, setCategory] = useState("all");
  const [lowOnly, setLowOnly] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjEdit, setAdjEdit] = useState(null); // {category, unit} prefill
  const [historyOpen, setHistoryOpen] = useState(null); // {category, unit}
  const [hideConfirm, setHideConfirm] = useState(null); // {category, unit}
  const [hiding, setHiding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (category !== "all") params.category = category;
      if (lowOnly) params.low_only = true;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const { data } = await api.get("/stock", { params });
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/stock/categories");
        setCategories(data.categories);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, category, lowOnly, activeProjectId, fromDate, toDate]);

  const confirmHide = async () => {
    if (!hideConfirm || !activeProjectId) return;
    setHiding(true);
    // Optimistic remove — makes the row disappear immediately
    const targetKey = `${hideConfirm.category}|${(hideConfirm.unit || "nos").toLowerCase()}`;
    if (data?.items) {
      setData({ ...data, items: data.items.filter((r) => r.key !== targetKey), total: Math.max(0, (data.total || 1) - 1) });
    }
    try {
      await api.post(`/projects/${activeProjectId}/hide-inventory-item`, {
        category: hideConfirm.category,
        unit: hideConfirm.unit,
      });
      toast.success(`Hidden: ${hideConfirm.category} (${hideConfirm.unit})`);
      setHideConfirm(null);
      // Refresh from server to keep summary + pagination consistent
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      // Rollback by reloading
      await load();
    } finally {
      setHiding(false);
    }
  };

  const summary = data?.summary || {};
  const totalPages = data?.total_pages || 1;

  return (
    <div className="p-8 sm:p-10 max-w-[1600px] mx-auto" data-testid="stock-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2 flex items-center gap-2">
            {activeProject && (
              <>
                <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: activeProject.color || "#0047FF" }} />
                <span>{activeProject.name}</span>
                <span className="text-neutral-300">·</span>
              </>
            )}
            <span>Inventory</span>
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Stock On Hand</h1>
          <p className="text-sm text-neutral-500 mt-2">
            Live computed across approved POs, outward issues, manual adjustments and inter-site transfers.
          </p>
        </div>
        <Button
          onClick={() => { setAdjEdit(null); setAdjOpen(true); }}
          className="rounded-sm h-11 px-5 bg-primary hover:bg-[#0039CC] text-white font-medium gap-2"
          data-testid="new-adjustment-btn"
        >
          <Plus size={16} weight="bold" /> New Adjustment
        </Button>
      </div>

      {/* Summary bento */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard
          testId="stat-keys"
          icon={<Package size={18} weight="bold" />}
          label="Tracked Items"
          value={summary.total_keys ?? "—"}
          sub={`${data?.total ?? 0} after filters`}
          accent="text-neutral-900"
        />
        <StatCard
          testId="stat-in"
          icon={<TrendUp size={18} weight="bold" />}
          label="Total Received"
          value={fmtQty(summary.total_in)}
          sub="Approved deliveries"
          accent="text-[#137333]"
        />
        <StatCard
          testId="stat-out"
          icon={<TrendDown size={18} weight="bold" />}
          label="Total Issued"
          value={fmtQty(summary.total_out)}
          sub="Outward to site / flats"
          accent="text-[#B06000]"
        />
        <StatCard
          testId="stat-value"
          icon={<CurrencyInr size={18} weight="bold" />}
          label={(fromDate || toDate) ? "Value In (Filtered)" : "Value In (Approved POs)"}
          value={fmtINR(summary.total_value_in || 0)}
          sub={(fromDate || toDate) ? `${fromDate || "…"} → ${toDate || "…"}` : "Qty × unit price"}
          accent="text-primary"
        />
        <StatCard
          testId="stat-low"
          icon={<Warning size={18} weight="bold" />}
          label="Low Stock"
          value={summary.low_count ?? 0}
          sub="Items at or below zero"
          accent="text-[#C5221F]"
        />
      </div>

      {/* Filters */}
      <div className="bg-white border border-neutral-200 rounded-sm p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[240px]">
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">Search</label>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPage(1);
                  setSearch(searchDraft);
                }
              }}
              placeholder="Search by category or unit"
              className="rounded-sm pl-9 h-10"
              data-testid="stock-search-input"
            />
          </div>
        </div>
        <div className="w-[200px]">
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">Category</label>
          <Select value={category} onValueChange={(v) => { setPage(1); setCategory(v); }}>
            <SelectTrigger className="rounded-sm h-10" data-testid="stock-category-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button
          onClick={() => { setPage(1); setLowOnly((v) => !v); }}
          className={`h-10 px-3 border rounded-sm text-sm flex items-center gap-2 transition-colors ${
            lowOnly ? "bg-[#FCE8E6] border-[#F3B2AC] text-[#C5221F]" : "bg-white border-neutral-200 text-neutral-700"
          }`}
          data-testid="low-only-toggle"
        >
          <Warning size={14} /> Low stock only
        </button>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 flex items-center gap-1">
            <Calendar size={11} /> From
          </label>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => { setPage(1); setFromDate(e.target.value); }}
            max={toDate || undefined}
            className="rounded-sm h-10 tabular-nums w-[160px]"
            data-testid="stock-from-date"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 flex items-center gap-1">
            <Calendar size={11} /> To
          </label>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => { setPage(1); setToDate(e.target.value); }}
            min={fromDate || undefined}
            className="rounded-sm h-10 tabular-nums w-[160px]"
            data-testid="stock-to-date"
          />
        </div>
        {(fromDate || toDate) && (
          <button
            className="h-10 px-2 text-xs text-neutral-500 hover:text-neutral-900"
            onClick={() => { setFromDate(""); setToDate(""); setPage(1); }}
            data-testid="stock-clear-dates"
          >
            Clear dates
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600">
              <tr>
                <th className="text-left px-6 py-3 font-medium text-xs uppercase tracking-wider">Category</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Unit</th>
                <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Opening Stock</th>
                <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Stock In</th>
                <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Stock Out</th>
                <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Damage</th>
                <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Available</th>
                <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">BOQ Limit</th>
                <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Ordered</th>
                <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Remaining</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider w-44">Progress</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="text-center py-12 text-neutral-400">Loading…</td></tr>
              ) : !data?.items?.length ? (
                <tr><td colSpan={12} className="text-center py-16 text-neutral-400">
                  No stock items match your filters. Click "New Adjustment" to add opening stock.
                </td></tr>
              ) : (
                data.items.map((it, idx) => (
                  <tr
                    key={it.key}
                    className={`border-t border-neutral-100 hover:bg-neutral-50 transition-colors ${idx % 2 ? "bg-neutral-50/40" : ""}`}
                    data-testid={`stock-row-${it.key}`}
                  >
                    <td className="px-6 py-3 font-medium text-neutral-900">{it.category}</td>
                    <td className="px-4 py-3 text-neutral-600 font-mono-num text-xs">{it.unit}</td>
                    <td className={`px-4 py-3 text-right tabular-nums ${it.opening_stock > 0 ? "text-[#3730A3]" : "text-neutral-400"}`} data-testid={`opening-${it.key}`}>{fmtQty(it.opening_stock)}</td>
                    <td className={`px-4 py-3 text-right tabular-nums ${it.stock_in > 0 ? "text-[#137333]" : "text-neutral-400"}`} data-testid={`stock-in-${it.key}`}>{fmtQty(it.stock_in)}</td>
                    <td className={`px-4 py-3 text-right tabular-nums ${it.stock_out > 0 ? "text-[#B06000]" : "text-neutral-400"}`} data-testid={`stock-out-${it.key}`}>{fmtQty(it.stock_out)}</td>
                    <td className={`px-4 py-3 text-right tabular-nums ${it.damage > 0 ? "text-[#C5221F]" : "text-neutral-400"}`} data-testid={`damage-${it.key}`}>{fmtQty(it.damage)}</td>
                    <td className={`px-4 py-3 text-right tabular-nums font-display font-bold text-base ${
                      it.low_stock ? "text-[#C5221F]" : "text-neutral-900"
                    }`}>
                      {fmtQty(it.available)}
                      {it.low_stock && (
                        <Warning size={12} weight="fill" className="inline ml-1 -mt-0.5 text-[#C5221F]" />
                      )}
                    </td>
                    <td className={`px-4 py-3 text-right tabular-nums ${it.boq_limit > 0 ? "text-neutral-900" : "text-neutral-300"}`} data-testid={`boq-limit-${it.key}`}>
                      {it.boq_limit > 0 ? fmtQty(it.boq_limit) : "—"}
                    </td>
                    <td className={`px-4 py-3 text-right tabular-nums ${it.ordered_to_date > 0 ? "text-neutral-600" : "text-neutral-300"}`} data-testid={`ordered-${it.key}`}>
                      {it.ordered_to_date > 0 ? fmtQty(it.ordered_to_date) : "—"}
                    </td>
                    <td className={`px-4 py-3 text-right tabular-nums font-medium ${it.over_budget ? "text-[#C5221F]" : it.boq_limit > 0 ? "text-[#137333]" : "text-neutral-300"}`} data-testid={`remaining-${it.key}`}>
                      {it.boq_limit > 0 ? fmtQty(it.remaining_budget) : "—"}
                    </td>
                    <td className="px-4 py-3" data-testid={`progress-${it.key}`}>
                      {it.boq_limit > 0 ? (
                        <BOQProgress percent={it.percent_ordered} overBudget={it.over_budget} />
                      ) : (
                        <span className="text-neutral-300 text-[11px]">no BOQ</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        className="p-1.5 text-neutral-500 hover:text-primary"
                        onClick={() => { setAdjEdit({ category: it.category, unit: it.unit }); setAdjOpen(true); }}
                        title="Adjust stock"
                        data-testid={`adjust-${it.key}`}
                      >
                        <PencilSimple size={16} />
                      </button>
                      <button
                        className="p-1.5 text-neutral-500 hover:text-primary"
                        onClick={() => setHistoryOpen({ category: it.category, unit: it.unit })}
                        title="View history"
                        data-testid={`history-${it.key}`}
                      >
                        <ClockCounterClockwise size={16} />
                      </button>
                      <button
                        className="p-1.5 text-neutral-500 hover:text-[#C5221F]"
                        onClick={() => setHideConfirm({ category: it.category, unit: it.unit })}
                        title="Hide from Stock-on-Hand (does not delete adjustments)"
                        data-testid={`hide-${it.key}`}
                      >
                        <Trash size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && data?.total > 0 && (
          <div className="border-t border-neutral-100 px-4 py-3 flex items-center justify-between text-xs text-neutral-500">
            <div className="tabular-nums">
              Showing <span className="font-medium text-neutral-700">{(page - 1) * pageSize + 1}</span>–
              <span className="font-medium text-neutral-700">{Math.min(page * pageSize, data.total)}</span> of{" "}
              <span className="font-medium text-neutral-700">{data.total}</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="h-8 w-8 border border-neutral-200 rounded-sm flex items-center justify-center disabled:opacity-40 hover:bg-neutral-50 disabled:hover:bg-white"
                data-testid="pagination-prev"
              >
                <CaretLeft size={14} />
              </button>
              <span className="px-3 tabular-nums font-medium text-neutral-700">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="h-8 w-8 border border-neutral-200 rounded-sm flex items-center justify-center disabled:opacity-40 hover:bg-neutral-50 disabled:hover:bg-white"
                data-testid="pagination-next"
              >
                <CaretRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      <AdjustmentDialog
        open={adjOpen}
        onOpenChange={setAdjOpen}
        categories={categories}
        prefill={adjEdit}
        projectId={activeProjectId}
        onSaved={() => { setAdjOpen(false); load(); }}
      />

      <HistoryDialog
        open={!!historyOpen}
        onOpenChange={(o) => !o && setHistoryOpen(null)}
        item={historyOpen}
        isAdmin={isAdmin}
        onChanged={load}
      />

      <Dialog open={!!hideConfirm} onOpenChange={(o) => !o && !hiding && setHideConfirm(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="hide-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Trash size={20} className="text-[#C5221F]" /> Hide this item?
            </DialogTitle>
            <DialogDescription className="text-sm text-neutral-600 pt-2">
              This will hide{" "}
              <span className="font-medium text-neutral-900">
                {hideConfirm?.category}
              </span>{" "}
              <span className="font-mono-num text-xs text-neutral-500">({hideConfirm?.unit})</span>{" "}
              from Stock-on-Hand for this project. Underlying adjustments, invoices and transfers are not touched — you can restore this item later from the project settings.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-3">
            <Button
              variant="ghost"
              className="rounded-sm"
              onClick={() => setHideConfirm(null)}
              disabled={hiding}
              data-testid="hide-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              className="rounded-sm bg-[#C5221F] hover:bg-[#A80D0B] text-white gap-1.5"
              onClick={confirmHide}
              disabled={hiding}
              data-testid="hide-confirm-btn"
            >
              <Trash size={14} weight="bold" />
              {hiding ? "Hiding…" : "Yes, hide it"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon, label, value, sub, accent, testId }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-sm p-5 hover:shadow-sm hover:-translate-y-[1px] transition-all duration-150" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">{label}</div>
        <div className="h-7 w-7 bg-neutral-100 flex items-center justify-center text-neutral-700">{icon}</div>
      </div>
      <div className={`font-display text-3xl font-bold tabular-nums ${accent}`}>{value}</div>
      <div className="text-xs text-neutral-500 mt-1">{sub}</div>
    </div>
  );
}

function AdjustmentDialog({ open, onOpenChange, categories, prefill, projectId, onSaved }) {
  const [form, setForm] = useState({
    category: "",
    unit: "nos",
    qty_change: "",
    type: "stock_in",
    notes: "",
    date: todayISO(),
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        category: prefill?.category || "",
        unit: prefill?.unit || "nos",
        qty_change: "",
        type: "stock_in",
        notes: "",
        date: todayISO(),
      });
    }
  }, [open, prefill]);

  const isSubtractive = SUBTRACTIVE_TYPES.has(form.type);
  const typeHints = {
    opening_stock: "Records the starting balance — adds to Opening Stock column.",
    stock_in: "Adds to the Stock In column (off-system receipts not yet captured by an approved PO).",
    stock_out: "Adds to the Stock Out column (off-system issuance not captured by an Outward entry).",
    damage: "Adds to the Damage column — reduces Available.",
  };

  const submit = async () => {
    const qty = parseFloat(form.qty_change);
    if (!form.category) return toast.error("Pick a category");
    if (!form.unit.trim()) return toast.error("Unit is required");
    if (isNaN(qty) || qty <= 0) return toast.error("Enter a positive quantity. Direction is determined by Type.");
    if (!projectId) return toast.error("Select a project from the sidebar first.");
    if (!form.date) return toast.error("Pick an adjustment date");
    setSaving(true);
    try {
      await api.post("/stock/adjustments", {
        category: form.category,
        unit: form.unit.trim(),
        qty_change: qty,
        type: form.type,
        notes: form.notes.trim(),
        project_id: projectId,
        date: form.date,
      });
      toast.success("Stock adjustment recorded");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Package size={20} /> {prefill ? "Adjust Stock" : "New Stock Adjustment"}
          </DialogTitle>
          <DialogDescription>
            Pick a Type — each maps to a column on the Stock-on-Hand table. Enter a positive quantity.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-neutral-600">Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="rounded-sm h-10" data-testid="adj-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-neutral-600">Unit *</Label>
              <Input
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                placeholder="bag / kg / nos / cum"
                className="rounded-sm h-10 font-mono-num"
                data-testid="adj-unit-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-neutral-600">Type *</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="rounded-sm h-10" data-testid="adj-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="opening_stock">Opening Stock</SelectItem>
                  <SelectItem value="stock_in">Stock In</SelectItem>
                  <SelectItem value="stock_out">Stock Out</SelectItem>
                  <SelectItem value="damage">Damage</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-neutral-600">
                Quantity * {isSubtractive ? "(subtracts)" : "(adds)"}
              </Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.qty_change}
                onChange={(e) => setForm({ ...form, qty_change: e.target.value })}
                placeholder="e.g. 100"
                className="rounded-sm h-10 text-right tabular-nums"
                data-testid="adj-qty-input"
              />
            </div>
          </div>

          <div className="text-[11px] text-neutral-500 -mt-1 px-0.5" data-testid="adj-type-hint">
            {typeHints[form.type]}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-neutral-600 flex items-center gap-1">
              <Calendar size={11} /> Adjustment Date *
            </Label>
            <Input
              type="date"
              value={form.date}
              max={todayISO()}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="rounded-sm h-10 tabular-nums"
              data-testid="adj-date-input"
            />
            <div className="text-[11px] text-neutral-500 px-0.5">
              Defaults to today. Back-date for physical events that happened earlier.
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-neutral-600">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Reason / reference / batch info"
              className="rounded-sm min-h-[80px]"
              data-testid="adj-notes-input"
            />
          </div>

          <Button
            className="w-full rounded-sm h-11 bg-primary hover:bg-[#0039CC] text-white font-medium"
            onClick={submit}
            disabled={saving}
            data-testid="adj-save-btn"
          >
            {saving ? "Recording…" : "Record Adjustment"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ open, onOpenChange, item, isAdmin, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const reload = React.useCallback(() => {
    if (!item) return;
    setLoading(true);
    const params = { category: item.category, unit: item.unit };
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    api.get("/stock/adjustments", { params })
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [item, fromDate, toDate]);

  useEffect(() => {
    if (open && item) {
      setFromDate("");
      setToDate("");
    }
  }, [open, item]);

  useEffect(() => {
    if (open) reload();
  }, [open, reload]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this adjustment?")) return;
    try {
      await api.delete(`/stock/adjustments/${id}`);
      toast.success("Adjustment deleted");
      setRows((prev) => prev.filter((r) => r.id !== id));
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <ClockCounterClockwise size={20} /> Adjustment History
          </DialogTitle>
          {item && (
            <DialogDescription>
              {item.category} · <span className="font-mono-num">{item.unit}</span>
            </DialogDescription>
          )}
        </DialogHeader>
        {/* Date filters */}
        <div className="flex flex-wrap items-end gap-2 py-2 border-y border-neutral-100">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1 flex items-center gap-1">
              <Calendar size={11} /> From
            </label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              max={toDate || undefined}
              className="rounded-sm h-9 w-[150px] tabular-nums"
              data-testid="history-from-date"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1 flex items-center gap-1">
              <Calendar size={11} /> To
            </label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              min={fromDate || undefined}
              className="rounded-sm h-9 w-[150px] tabular-nums"
              data-testid="history-to-date"
            />
          </div>
          {(fromDate || toDate) && (
            <button
              className="h-9 px-2 text-xs text-neutral-500 hover:text-neutral-900"
              onClick={() => { setFromDate(""); setToDate(""); }}
              data-testid="history-clear-dates"
            >
              Clear
            </button>
          )}
          <span className="ml-auto text-[11px] text-neutral-500 tabular-nums">
            {rows.length} record{rows.length === 1 ? "" : "s"}
          </span>
        </div>
        {loading ? (
          <div className="text-center py-8 text-neutral-400 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-8 text-neutral-400 text-sm">
            {(fromDate || toDate) ? "No adjustments in the selected date range." : "No manual adjustments recorded yet."}
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto -mx-6 px-6">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
                <tr>
                  <th className="text-left py-2 font-medium">Date</th>
                  <th className="text-left py-2 font-medium">Type</th>
                  <th className="text-right py-2 font-medium">Qty Change</th>
                  <th className="text-left py-2 font-medium">By</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const meta = TYPE_META[r.type] || { label: r.type, color: "bg-neutral-100 text-neutral-700 border-neutral-300" };
                  const subtractive = SUBTRACTIVE_TYPES.has(r.type);
                  const displayDate = r.date || (r.created_at ? new Date(r.created_at).toISOString().slice(0, 10) : "—");
                  return (
                    <tr key={r.id} className="border-b border-neutral-100" data-testid={`history-row-${r.id}`}>
                      <td className="py-2 text-neutral-600 tabular-nums whitespace-nowrap">
                        {displayDate}
                        {!r.date && <span className="ml-1 text-[9px] uppercase text-neutral-400" title="Legacy record — no explicit date">legacy</span>}
                      </td>
                      <td className="py-2">
                        <span className={`inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${meta.color}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className={`py-2 text-right tabular-nums font-medium ${subtractive ? "text-[#C5221F]" : "text-[#137333]"}`}>
                        {subtractive ? "−" : "+"}{fmtQty(Math.abs(r.qty_change))}
                      </td>
                      <td className="py-2 text-neutral-600">{r.created_by_name || "—"}</td>
                      <td className="py-2 text-right">
                        {isAdmin && (
                          <button
                            onClick={() => handleDelete(r.id)}
                            className="text-neutral-400 hover:text-destructive p-1"
                            data-testid={`history-delete-${r.id}`}
                          >
                            <Trash size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {rows.some((r) => r.notes) && (
                  <tr>
                    <td colSpan={5} className="py-3">
                      <div className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1">Notes</div>
                      <div className="space-y-1">
                        {rows.filter((r) => r.notes).map((r) => (
                          <div key={`n-${r.id}`} className="text-xs text-neutral-600 border-l-2 border-neutral-200 pl-2">
                            <span className="text-neutral-400">[{r.date || (r.created_at || "").slice(0,10)}]</span> {r.notes}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
